import {
  AfterViewInit,
  Component,
  effect,
  ElementRef,
  inject,
  input,
  OnDestroy,
  output,
  PLATFORM_ID,
  untracked,
  viewChild,
  ViewEncapsulation,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { IOutlet, UiContextService } from '@cakeiteasy/web-utils';
import type * as MapLibre from 'maplibre-gl';
import {
  configureMapLibreWorker,
  createBounds,
  getMapLibreOutletMarkerHtml,
  hasCoordinates,
  latLng,
  simplifyMapLibreOutletStyle,
} from '../../../helpers/maplibre.helper';
import { DEFAULT_MAPLIBRE_CONFIG } from '../../../constants/maplibre.constants';
import { MapMarkerThemeService } from '../../../services/map-marker-theme.service';

const OUTLET_PANEL_WIDTH = { md: 230, lg: 285 };
const OUTLET_PADDING = 20;

@Component({
  standalone: true,
  selector: 'ui-map-for-outlets-accordion',
  templateUrl: './ui-map-for-outlets-accordion.component.html',
  encapsulation: ViewEncapsulation.None,
})
export class UiMapForOutletsAccordionComponent implements AfterViewInit, OnDestroy {
  readonly outlets = input<IOutlet[]>();
  readonly disabledIds = input<number[]>([]);
  readonly selectedOutlet = input<IOutlet>();
  readonly shiftMapToRight = input(false);
  readonly initialMaxZoom = input<number>();

  readonly onSelectOutletOnMap = output<IOutlet>();

  private readonly _mapContainer = viewChild.required<ElementRef<HTMLDivElement>>('mapContainer');

  private readonly _uiContext = inject(UiContextService);
  private readonly _markerTheme = inject(MapMarkerThemeService);
  private readonly _platformId = inject(PLATFORM_ID);

  private _maplibre: typeof MapLibre;
  private _map: MapLibre.Map;
  private _markers = new Map<number, MapLibre.Marker>();
  private _isViewInitialized = false;
  private _isMapInitializing = false;
  private _isDestroyed = false;

  constructor() {
    effect(() => {
      const outlets = this.outlets();

      if (outlets) {
        untracked(() => this._onOutletsChanged(outlets));
      }
    });

    effect(() => {
      const outlet = this.selectedOutlet();
      untracked(() => this._onSelectedOutletChanged(outlet));
    });

    effect(() => {
      this.disabledIds();
      this._markerTheme.theme();
      untracked(() => this._renderMarkers());
    });
  }

  async ngAfterViewInit(): Promise<void> {
    if (!isPlatformBrowser(this._platformId)) {
      return;
    }

    this._isViewInitialized = true;
    await this._tryInitMap();
  }

  ngOnDestroy(): void {
    this._isDestroyed = true;
    this._clearMarkers();
    this._map?.remove();
  }

  private _onOutletsChanged(outlets: IOutlet[]): void {
    if (!this._map) {
      void this._tryInitMap();
      return;
    }

    this._renderMarkers();

    if (this.selectedOutlet()) {
      this._setCenter(this.selectedOutlet());
    } else {
      this._fitBounds(outlets);
    }
  }

  private _onSelectedOutletChanged(outlet: IOutlet | undefined): void {
    if (!this._map) {
      void this._tryInitMap();
      return;
    }

    this._renderMarkers();

    if (outlet) {
      this._setCenter(outlet);
    } else if (this.outlets()) {
      this._fitBounds(this.outlets());
    }
  }

  private _isActive(outletId: number): boolean {
    return this.selectedOutlet()?.id === outletId;
  }

  private _isDisabled(outletId: number): boolean {
    return Boolean(this.disabledIds()?.includes(outletId));
  }

  private _renderMarkers(): void {
    if (!this._map || !this._maplibre || !this.outlets()) {
      return;
    }

    this._clearMarkers();

    this.outlets().filter(hasCoordinates).forEach((outlet) => {
      const isActive = this._isActive(outlet.id);
      const isDisabled = this._isDisabled(outlet.id);
      const element = document.createElement('div');
      element.title = outlet.name;
      element.innerHTML = getMapLibreOutletMarkerHtml(
        outlet.name,
        this._markerTheme.getMarkerOptions(isActive, isDisabled),
      );
      element.style.zIndex = isActive ? '1000' : '0';
      element.style.pointerEvents = isDisabled ? 'none' : 'auto';

      if (!isDisabled) {
        element.addEventListener('click', () => this.onSelectOutletOnMap.emit(outlet));
      }

      const marker = new this._maplibre.Marker({ element, anchor: 'center' })
        .setLngLat(latLng(outlet as { latitude: number; longitude: number }));

      marker.addTo(this._map);
      this._markers.set(outlet.id, marker);
    });
  }

  private _clearMarkers(): void {
    this._markers.forEach((marker) => marker.remove());
    this._markers.clear();
  }

  private async _tryInitMap(): Promise<void> {
    if (
      this._isDestroyed ||
      !this._isViewInitialized ||
      this._map ||
      this._isMapInitializing ||
      !isPlatformBrowser(this._platformId)
    ) {
      return;
    }

    const initialView = this._getInitialView();

    if (!initialView) {
      return;
    }

    try {
      this._isMapInitializing = true;
      this._maplibre = await import('maplibre-gl');

      if (this._isDestroyed) {
        return;
      }

      configureMapLibreWorker(this._maplibre);
      const { styleUrl, maxZoom } = DEFAULT_MAPLIBRE_CONFIG;
      this._map = new this._maplibre.Map({
        container: this._mapContainer().nativeElement,
        style: styleUrl,
        maxZoom,
        scrollZoom: false,
        attributionControl: { compact: true },
        ...initialView,
      });
      simplifyMapLibreOutletStyle(this._map);

      this._map.addControl(
        new this._maplibre.NavigationControl({ showCompass: false }),
        !this._uiContext.isSm() ? 'bottom-right' : 'bottom-left',
      );

      this._renderMarkers();

      if ('center' in initialView) {
        setTimeout(() => {
          if (!this._isDestroyed && this._map) {
            this._panToCenter(this._map);
          }
        }, 50);
      }
    } finally {
      this._isMapInitializing = false;
    }
  }

  private _getInitialView(): Partial<MapLibre.MapOptions> {
    const outlets = this.outlets();
    const targetOutlet = this.selectedOutlet() || (outlets?.length === 1 ? outlets[0] : null);

    if (targetOutlet && hasCoordinates(targetOutlet)) {
      return {
        center: latLng(targetOutlet as { latitude: number; longitude: number }),
        zoom: DEFAULT_MAPLIBRE_CONFIG.initialZoom,
      };
    }

    const positionedOutlets = outlets?.filter(hasCoordinates) || [];

    if (positionedOutlets.length > 1) {
      return {
        bounds: this._getBoundsLike(positionedOutlets),
        fitBoundsOptions: {
          padding: this._getFitBoundsPadding(),
          maxZoom: this.initialMaxZoom(),
          duration: 0,
        },
      };
    }

    return null;
  }

  private _getBoundsLike(outlets: IOutlet[]): [[number, number], [number, number]] {
    let minLat = Number.POSITIVE_INFINITY;
    let minLng = Number.POSITIVE_INFINITY;
    let maxLat = Number.NEGATIVE_INFINITY;
    let maxLng = Number.NEGATIVE_INFINITY;

    outlets.forEach((outlet) => {
      minLat = Math.min(minLat, outlet.latitude);
      minLng = Math.min(minLng, outlet.longitude);
      maxLat = Math.max(maxLat, outlet.latitude);
      maxLng = Math.max(maxLng, outlet.longitude);
    });

    return [[minLng, minLat], [maxLng, maxLat]];
  }

  private _getFitBoundsPadding(): MapLibre.PaddingOptions {
    return {
      top: 60,
      left: this.shiftMapToRight() ? 320 : 60,
      bottom: 60,
      right: 60,
    };
  }

  private _setCenter(outlet: IOutlet) {
    if (!this._map || !hasCoordinates(outlet)) {
      return;
    }

    setTimeout(() => {
      this._map.jumpTo({
        center: latLng(outlet as { latitude: number; longitude: number }),
        zoom: DEFAULT_MAPLIBRE_CONFIG.initialZoom,
      });
      this._panToCenter(this._map);
    }, 50);
  }

  private _fitBounds(outlets: IOutlet[]) {
    if (!this._map || !this._maplibre) {
      return;
    }

    const positionedOutlets = outlets.filter(hasCoordinates);

    if (!positionedOutlets.length) {
      return;
    }

    if (positionedOutlets.length === 1) {
      this._setCenter(positionedOutlets[0]);
      return;
    }

    const bounds = createBounds(this._maplibre, positionedOutlets);

    setTimeout(() => {
      if (this.selectedOutlet()) {
        this._setCenter(this.selectedOutlet());
        return;
      }

      this._map.fitBounds(bounds, {
        padding: this._getFitBoundsPadding(),
        maxZoom: this.initialMaxZoom(),
        duration: 800,
      });
    }, 50);
  }

  private _panToCenter(map: MapLibre.Map) {
    let offsetX = 0;

    if (this._uiContext.isLg() || this._uiContext.isXl()) {
      offsetX = -1 * Math.round((OUTLET_PANEL_WIDTH.lg + OUTLET_PADDING) / 2);
    }

    if (this._uiContext.isMd()) {
      offsetX = -1 * Math.round((OUTLET_PANEL_WIDTH.md + OUTLET_PADDING) / 2);
    }

    map.panBy([offsetX, 0], { duration: 800 });
  }
}
